import React, { useState, useEffect, ReactNode } from 'react';
import LoadingIcon from '../LoadingIcon/LoadingIcon';

// Define the props for LoadingWrapper with generics.
// The component can either manage the async operation internally
// or use externally provided loading state and data.
interface LoadingWrapperProps<T> {
  // Optional async operation for internal state management.
  asyncOperation?: () => Promise<T>;
  // External loading flag to control display of <LoadingIcon />.
  loading?: boolean;
  /** Shown below the spinner while loading (controlled mode only). */
  loadingMessage?: string;
  // External data provided when loading is controlled externally.
  externalData?: T;
  // Children can either be a render function that receives the data
  // or static React nodes. Optional when used as a standalone spinner.
  children?: ((data: T) => ReactNode) | ReactNode;

  style?: React.CSSProperties;
}

function LoadingWrapper<T>({
  asyncOperation,
  loading: externalLoading,
  loadingMessage,
  externalData,
  children,
  style
}: LoadingWrapperProps<T>): JSX.Element {
  // Determine if the component is in external (controlled) mode.
  const isControlled = typeof externalLoading === 'boolean';

  // Internal state for data and loading if asyncOperation is provided.
  const [internalData, setInternalData] = useState<T | null>(null);
  const [internalLoading, setInternalLoading] = useState<boolean>(asyncOperation ? true : false);

  useEffect(() => {
    // If no asyncOperation is provided, do nothing.
    if (!asyncOperation) return;

    let isMounted = true;
    asyncOperation()
      .then((result) => {
        if (isMounted) {
          setInternalData(result);
          setInternalLoading(false);
        }
      })
      .catch((error) => {
        console.error('Async operation error:', error);
        if (isMounted) {
          setInternalLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [asyncOperation]);

  // Use external values if in controlled mode; otherwise use internal state.
  const loading = isControlled ? externalLoading : internalLoading;
  const data = isControlled ? externalData : internalData;

  // While loading, show the loading icon.
  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: loadingMessage ? 'column' : 'row',
          justifyContent: 'center',
          alignItems: 'center',
          gap: loadingMessage ? 12 : undefined,
          ...style,
        }}
      >
        <LoadingIcon />
        {loadingMessage ? (
          <p style={{ margin: 0, textAlign: 'center', color: 'var(--text-secondary, #5c6b7a)', fontSize: '0.95rem' }}>
            {loadingMessage}
          </p>
        ) : null}
      </div>
    );
  }

  // Render children: if it's a function, pass the data; otherwise, render as is.
  if (typeof children === 'function') {
    return <>{(children as (data: T) => ReactNode)(data as T)}</>;
  }

  return <>{children ?? null}</>;
}

export default LoadingWrapper;
